import { useState, useEffect, useMemo, useRef, SyntheticEvent } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  LinearProgress,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Card,
  CardContent,
  CardActionArea,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  ImageList,
  ImageListItem,
  TextField,
  InputAdornment,
  Toolbar,
  AppBar,
  useTheme,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import MenuIcon from '@mui/icons-material/Menu';
import api from '../services/api';
import { API_BASE_URL } from '../utils/imageUrl';
import { PageName, TaskType, ApiResponse, User } from '../types';
import { DrawerNavigation } from '../components';
import { getUserData, isAdmin as checkIsAdmin } from '../utils/cookies';
import { DRAWER_WIDTH } from '../constants/ui';
import { TASK_TYPE_CONFIG, DATASET_TYPE_CONFIG, TaskTypeConfigItem, getTaskTypeConfig, getTaskBgColor } from '../constants/taskConfig';

// Base URL for images (no JWT required)
const IMAGE_BASE_URL = `${API_BASE_URL}images/type`;

const MAX_VISIBLE_ITEMS = 10;

// Types for API responses
interface TaskData {
  id: string;
  title?: string;
  description?: string;
  type: TaskType;
  samplesCount: number;
  completedCount: number;
  noActionCount: number;
  createdAt?: number;
  updatedAt?: number;
  createdBy?: {
    firstName: string;
    lastName: string;
  };
  finished?: boolean;
}

interface DatasetData {
  id: string;
  label: string;
  type: TaskType;
}

interface DatasetDetails {
  id: string;
  label: string;
  type: TaskType;
  randomSamples: string[];
  error?: boolean;
}

interface DashboardProps {
  onLogout: () => void;
  onNavigate: (page: PageName, taskId?: string) => void;
}

function Dashboard({ onLogout, onNavigate }: DashboardProps) {
  const theme = useTheme();
  const [user, setUser] = useState<User | null>(null);
  const [tasks, setTasks] = useState<TaskData[]>([]);
  const [datasets, setDatasets] = useState<DatasetData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  // Drawer state
  const [mobileOpen, setMobileOpen] = useState<boolean>(false);

  const handleDrawerToggle = (): void => {
    setMobileOpen(!mobileOpen);
  };

  // Search states
  const [taskSearch, setTaskSearch] = useState<string>('');
  const [datasetSearch, setDatasetSearch] = useState<string>('');

  // Type filter states (null means all types)
  const [taskTypeFilter, setTaskTypeFilter] = useState<TaskType | null>(null);
  const [datasetTypeFilter, setDatasetTypeFilter] = useState<TaskType | null>(null);

  // Visible items count
  const [tasksVisible, setTasksVisible] = useState<number>(MAX_VISIBLE_ITEMS);
  const [datasetsVisible, setDatasetsVisible] = useState<number>(MAX_VISIBLE_ITEMS);

  // Dataset detail dialog state
  const [selectedDataset, setSelectedDataset] = useState<DatasetData | null>(null);
  const [datasetDetails, setDatasetDetails] = useState<DatasetDetails | null>(null);
  const [detailsLoading, setDetailsLoading] = useState<boolean>(false);

  // Ref to prevent double fetching in StrictMode
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchData = async (): Promise<void> => {
      try {
        setLoading(true);
        setError('');

        // Get user data from cookies (stored at login)
        const storedUser = getUserData();
        if (storedUser) {
          setUser({
            id: storedUser.id,
            username: storedUser.username,
            firstName: storedUser.firstName,
            lastName: storedUser.lastName,
            roles: storedUser.roles,
          });
        }

        // Fetch tasks
        const tasksResponse = await api.get<ApiResponse<TaskData[]>>('tasks/list/my');

        // Sort tasks: finished last, then by updatedAt (most recent first)
        const sortedTasks = (tasksResponse.data.results || [])
          .sort((a, b) => {
            // Finished tasks go last
            if (a.finished !== b.finished) {
              return a.finished ? 1 : -1;
            }
            // Then sort by updatedAt (most recent first)
            return (b.updatedAt || 0) - (a.updatedAt || 0);
          });
        setTasks(sortedTasks);

        // If user is admin, fetch datasets from individual endpoints
        if (checkIsAdmin()) {
          try {
            const [detResponse, mmcResponse, plateResponse] = await Promise.all([
              api.get<ApiResponse<DatasetData[]>>('datasets/list/detection'),
              api.get<ApiResponse<DatasetData[]>>('datasets/list/mmc'),
              api.get<ApiResponse<DatasetData[]>>('datasets/list/plate'),
            ]);

            // Combine all datasets with their type and sort alphabetically by label
            const allDatasets: DatasetData[] = [
              ...(detResponse.data.results || []).map((d) => ({ ...d, type: 'detection' as TaskType })),
              ...(mmcResponse.data.results || []).map((d) => ({ ...d, type: 'mmc' as TaskType })),
              ...(plateResponse.data.results || []).map((d) => ({ ...d, type: 'plate' as TaskType })),
            ].sort((a, b) => a.label.localeCompare(b.label));

            setDatasets(allDatasets);
          } catch (datasetErr) {
            console.error('Failed to fetch datasets:', datasetErr);
          }
        }
      } catch (err) {
        console.error('Failed to fetch data:', err);
        setError('Failed to load dashboard data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const calculateProgress = (task: TaskData): { percentage: number; hasNoSamples: boolean } => {
    if (task.samplesCount === 0) {
      return { percentage: 0, hasNoSamples: true };
    }
    const percentage = Math.round(((task.completedCount + task.noActionCount) / task.samplesCount) * 100);
    return { percentage, hasNoSamples: false };
  };

  // Filtered tasks (all matching)
  const allFilteredTasks = useMemo(() => {
    const searchLower = taskSearch.toLowerCase().trim();
    return tasks.filter((task) => {
      const matchesSearch = !searchLower ||
        (task.title || '').toLowerCase().includes(searchLower) ||
        (task.description || '').toLowerCase().includes(searchLower);
      const matchesType = !taskTypeFilter || task.type === taskTypeFilter;
      return matchesSearch && matchesType;
    });
  }, [tasks, taskSearch, taskTypeFilter]);

  // Visible filtered tasks (limited by tasksVisible)
  const filteredTasks = useMemo(() => {
    return allFilteredTasks.slice(0, tasksVisible);
  }, [allFilteredTasks, tasksVisible]);

  // Filtered datasets (all matching)
  const allFilteredDatasets = useMemo(() => {
    const searchLower = datasetSearch.toLowerCase().trim();
    return datasets.filter((dataset) => {
      const matchesSearch = !searchLower ||
        dataset.label?.toLowerCase().includes(searchLower);
      const matchesType = !datasetTypeFilter || dataset.type === datasetTypeFilter;
      return matchesSearch && matchesType;
    });
  }, [datasets, datasetSearch, datasetTypeFilter]);

  // Visible filtered datasets (limited by datasetsVisible)
  const filteredDatasets = useMemo(() => {
    return allFilteredDatasets.slice(0, datasetsVisible);
  }, [allFilteredDatasets, datasetsVisible]);

  // Reset visible count when search or filter changes
  useEffect(() => {
    setTasksVisible(MAX_VISIBLE_ITEMS);
  }, [taskSearch, taskTypeFilter]);

  useEffect(() => {
    setDatasetsVisible(MAX_VISIBLE_ITEMS);
  }, [datasetSearch, datasetTypeFilter]);

  const handleDatasetClick = async (dataset: DatasetData): Promise<void> => {
    setSelectedDataset(dataset);
    setDetailsLoading(true);
    setDatasetDetails(null);

    try {
      // Use search endpoint with random sorting to get sample images
      const searchEndpoint = `${dataset.type}/search`;
      const searchPayload = {
        datasets: [dataset.id],
        sorting: 'random',
        pageSize: 20,
        page: 1,
      };
      const response = await api.post<ApiResponse<{ items: { id: string }[] }>>(searchEndpoint, searchPayload);
      setDatasetDetails({
        id: dataset.id,
        label: dataset.label,
        type: dataset.type,
        randomSamples: (response.data.results?.items || []).map((item) => item.id),
      });
    } catch (err) {
      console.error('Failed to fetch dataset details:', err);
      setDatasetDetails({ id: dataset.id, label: dataset.label, type: dataset.type, randomSamples: [], error: true });
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleCloseDialog = (): void => {
    setSelectedDataset(null);
    setDatasetDetails(null);
  };

  const handleAccessDataset = (): void => {
    if (!selectedDataset) return;
    // Navigate to the appropriate search page based on dataset type
    const pageMap: Record<TaskType, PageName> = {
      mmc: 'searchMmc',
      detection: 'searchDet',
      plate: 'searchPlate',
    };
    const targetPage = pageMap[selectedDataset.type];
    if (targetPage) {
      onNavigate(targetPage);
    }
    handleCloseDialog();
  };

  const getImageUrl = (type: TaskType, imageId: string, width = 200, height = 200): string => {
    return `${IMAGE_BASE_URL}/${type}/${imageId}?width=${width}&height=${height}`;
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" noWrap component="div">
              Welcome{user ? `, ${user.firstName}` : ''}!
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {user && (
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1, mr: 2 }}>
                <Typography variant="body2">@{user.username}</Typography>
                {user.roles?.map((role) => (
                  <Chip
                    key={role}
                    label={role}
                    size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                  />
                ))}
              </Box>
            )}
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              startIcon={<LogoutIcon />}
              onClick={onLogout}
            >
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Side Navigation Drawer */}
      <DrawerNavigation
        currentPage="dashboard"
        onNavigate={onNavigate}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
          bgcolor: 'background.default',
        }}
      >
        <Toolbar /> {/* Spacer for AppBar */}
        <Container maxWidth="lg" disableGutters>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Tasks Section */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5" component="h2">
            Your Tasks
            {tasks.length > 0 && (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                (showing {filteredTasks.length} of {allFilteredTasks.length}{(taskSearch || taskTypeFilter) && ` matching`})
              </Typography>
            )}
          </Typography>
          {tasks.length > 0 && (
            <TextField
              size="small"
              placeholder="Search tasks..."
              value={taskSearch}
              onChange={(e) => setTaskSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 250 }}
            />
          )}
        </Box>

        {/* Task Type Filters */}
        {tasks.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
            <Chip
              label="All"
              size="small"
              variant={taskTypeFilter === null ? 'filled' : 'outlined'}
              onClick={() => setTaskTypeFilter(null)}
              sx={{
                bgcolor: taskTypeFilter === null ? 'primary.main' : 'transparent',
                color: taskTypeFilter === null ? 'white' : 'text.primary',
                '&:hover': { bgcolor: taskTypeFilter === null ? 'primary.dark' : 'action.hover' },
              }}
            />
            {(Object.entries(TASK_TYPE_CONFIG) as [TaskType, TaskTypeConfigItem][]).map(([type, config]) => (
              <Chip
                key={type}
                icon={<config.icon size={16} />}
                label={config.label}
                size="small"
                variant={taskTypeFilter === type ? 'filled' : 'outlined'}
                onClick={() => setTaskTypeFilter(taskTypeFilter === type ? null : type)}
                sx={{
                  bgcolor: taskTypeFilter === type ? config.color : 'transparent',
                  color: taskTypeFilter === type ? 'white' : 'text.primary',
                  borderColor: config.color,
                  '&:hover': { bgcolor: taskTypeFilter === type ? config.color : getTaskBgColor(type, theme.palette.mode) },
                  '& .MuiChip-icon': { color: taskTypeFilter === type ? 'white' : config.color },
                }}
              />
            ))}
          </Box>
        )}

        {tasks.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No tasks assigned to you yet.
            </Typography>
          </Paper>
        ) : filteredTasks.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No tasks match your search.
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {filteredTasks.map((task) => {
              const config = getTaskTypeConfig(task.type);
              const IconComponent = config.icon;
              const { percentage, hasNoSamples } = calculateProgress(task);

              return (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={task.id}>
                  <Card
                    elevation={task.finished ? 1 : 2}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      opacity: task.finished ? 0.6 : 1,
                      '&:hover': {
                        transform: task.finished ? 'none' : 'translateY(-4px)',
                        boxShadow: task.finished ? 1 : 6,
                      },
                    }}
                  >
                    <CardActionArea sx={{ flexGrow: 1 }} onClick={() => onNavigate('taskPage', task.id)}>
                      <CardContent>
                        {/* Task Type Header */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            mb: 2,
                          }}
                        >
                          <Box
                            sx={{
                              p: 1,
                              borderRadius: 2,
                              bgcolor: getTaskBgColor(task.type, theme.palette.mode),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <IconComponent size={40} />
                          </Box>
                          <Chip
                            label={config.label}
                            size="small"
                            sx={{
                              bgcolor: config.color,
                              color: 'white',
                              fontWeight: 500,
                            }}
                          />
                          {task.finished && (
                            <Chip
                              label="Finished"
                              size="small"
                              sx={{
                                bgcolor: 'action.disabledBackground',
                                color: '#666',
                                fontWeight: 500,
                                fontSize: '0.7rem',
                              }}
                            />
                          )}
                        </Box>

                        {/* Task Title */}
                        <Typography variant="h6" component="h3" gutterBottom>
                          {task.title || 'Untitled Task'}
                        </Typography>

                        {/* Task Description */}
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2, minHeight: 40 }}
                        >
                          {task.description || 'No description'}
                        </Typography>

                        {/* Created By */}
                        {task.createdBy && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mb: 2 }}
                          >
                            Created by: {task.createdBy.firstName} {task.createdBy.lastName}
                          </Typography>
                        )}

                        {/* Progress Section */}
                        <Box sx={{ mt: 'auto' }}>
                          {hasNoSamples ? (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontStyle: 'italic', textAlign: 'center', py: 1 }}
                            >
                              No samples assigned
                            </Typography>
                          ) : (
                            <>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  mb: 1,
                                }}
                              >
                                <Typography variant="body2" fontWeight={500}>
                                  Progress
                                </Typography>
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  sx={{ color: config.color }}
                                >
                                  {percentage}%
                                </Typography>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={percentage}
                                sx={{
                                  height: 8,
                                  borderRadius: 4,
                                  bgcolor: getTaskBgColor(task.type, theme.palette.mode),
                                  '& .MuiLinearProgress-bar': {
                                    borderRadius: 4,
                                    bgcolor: config.color,
                                  },
                                }}
                              />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mt: 0.5, textAlign: 'right' }}
                              >
                                {task.completedCount + task.noActionCount} / {task.samplesCount.toLocaleString()} samples
                              </Typography>
                            </>
                          )}
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* Show more tasks button */}
        {allFilteredTasks.length > filteredTasks.length && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => setTasksVisible((prev) => prev + MAX_VISIBLE_ITEMS)}
            >
              Show more ({allFilteredTasks.length - filteredTasks.length} remaining)
            </Button>
          </Box>
        )}

        {/* Datasets Section - Admin Only */}
        {datasets.length > 0 && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 5, mb: 2, flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="h5" component="h2">
                Datasets
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  (showing {filteredDatasets.length} of {allFilteredDatasets.length}{(datasetSearch || datasetTypeFilter) && ` matching`})
                </Typography>
              </Typography>
              <TextField
                size="small"
                placeholder="Search datasets..."
                value={datasetSearch}
                onChange={(e) => setDatasetSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 250 }}
              />
            </Box>

            {/* Dataset Type Filters */}
            <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
              <Chip
                label="All"
                size="small"
                variant={datasetTypeFilter === null ? 'filled' : 'outlined'}
                onClick={() => setDatasetTypeFilter(null)}
                sx={{
                  bgcolor: datasetTypeFilter === null ? 'primary.main' : 'transparent',
                  color: datasetTypeFilter === null ? 'white' : 'text.primary',
                  '&:hover': { bgcolor: datasetTypeFilter === null ? 'primary.dark' : 'action.hover' },
                }}
              />
              {(Object.entries(DATASET_TYPE_CONFIG) as [TaskType, typeof DATASET_TYPE_CONFIG[TaskType]][]).map(([type, config]) => (
                <Chip
                  key={type}
                  icon={<config.icon size={16} />}
                  label={config.label.replace(' Datasets', '')}
                  size="small"
                  variant={datasetTypeFilter === type ? 'filled' : 'outlined'}
                  onClick={() => setDatasetTypeFilter(datasetTypeFilter === type ? null : type)}
                  sx={{
                    bgcolor: datasetTypeFilter === type ? config.color : 'transparent',
                    color: datasetTypeFilter === type ? 'white' : 'text.primary',
                    borderColor: config.color,
                    '&:hover': { bgcolor: datasetTypeFilter === type ? config.color : getTaskBgColor(type, theme.palette.mode) },
                    '& .MuiChip-icon': { color: datasetTypeFilter === type ? 'white' : config.color },
                  }}
                />
              ))}
            </Box>

            {filteredDatasets.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No datasets match your search.
                </Typography>
              </Paper>
            ) : (
              <Grid container spacing={2}>
                {filteredDatasets.map((dataset) => {
                  const config = DATASET_TYPE_CONFIG[dataset.type];
                  const IconComponent = config?.icon;

                  return (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={dataset.id}>
                      <Card
                        elevation={1}
                        sx={{
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: 4,
                          },
                        }}
                      >
                        <CardActionArea onClick={() => handleDatasetClick(dataset)}>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                              <Box
                                sx={{
                                  p: 1,
                                  borderRadius: 1,
                                  bgcolor: getTaskBgColor(dataset.type, theme.palette.mode),
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                {IconComponent && <IconComponent size={32} />}
                              </Box>
                              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                <Typography variant="body1" fontWeight={500} noWrap>
                                  {dataset.label}
                                </Typography>
                                <Chip
                                  label={config?.label?.replace(' Datasets', '')}
                                  size="small"
                                  sx={{
                                    bgcolor: config?.color,
                                    color: 'white',
                                    fontWeight: 500,
                                    fontSize: '0.65rem',
                                    height: 20,
                                  }}
                                />
                              </Box>
                            </Box>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}

            {/* Show more datasets button */}
            {allFilteredDatasets.length > filteredDatasets.length && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Button
                  variant="outlined"
                  onClick={() => setDatasetsVisible((prev) => prev + MAX_VISIBLE_ITEMS)}
                >
                  Show more ({allFilteredDatasets.length - filteredDatasets.length} remaining)
                </Button>
              </Box>
            )}
          </>
        )}

        {/* Dataset Details Dialog */}
        <Dialog
          open={Boolean(selectedDataset)}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          {selectedDataset && (
            <>
              <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      bgcolor: getTaskBgColor(selectedDataset.type, theme.palette.mode),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {(() => {
                      const Icon = DATASET_TYPE_CONFIG[selectedDataset.type]?.icon;
                      return Icon ? <Icon size={32} /> : null;
                    })()}
                  </Box>
                  <Box>
                    <Typography variant="h6">{selectedDataset.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {DATASET_TYPE_CONFIG[selectedDataset.type]?.label}
                    </Typography>
                  </Box>
                </Box>
                <IconButton onClick={handleCloseDialog} size="small">
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers>
                {detailsLoading ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                    <CircularProgress size={40} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      Loading dataset details...
                    </Typography>
                  </Box>
                ) : datasetDetails?.error ? (
                  <Alert severity="error">Failed to load dataset details.</Alert>
                ) : datasetDetails ? (
                  <>
                    <Typography variant="subtitle2" gutterBottom>
                      Random Samples
                    </Typography>
                    <ImageList cols={4} gap={8} sx={{ maxHeight: 400 }}>
                      {datasetDetails.randomSamples.map((imageId) => (
                        <ImageListItem key={imageId}>
                          <Box
                            component="img"
                            src={getImageUrl(selectedDataset.type, imageId, 200, 200)}
                            alt={`Sample ${imageId}`}
                            loading="lazy"
                            sx={{
                              width: '100%',
                              height: 150,
                              objectFit: 'cover',
                              borderRadius: 1,
                              bgcolor: 'grey.200',
                            }}
                            onError={(e: SyntheticEvent<HTMLImageElement>) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        </ImageListItem>
                      ))}
                    </ImageList>
                  </>
                ) : null}
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseDialog} color="inherit">
                  Close
                </Button>
                <Button
                  onClick={handleAccessDataset}
                  variant="contained"
                  startIcon={<OpenInNewIcon />}
                  disabled={detailsLoading || datasetDetails?.error}
                >
                  Access Dataset
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
        </Container>
      </Box>
    </Box>
  );
}

export default Dashboard;
